import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { useAuth } from "./context/AuthContext";
import { getUserProfile, isInvestor } from "./services/userApi";
import { usePageTracking } from "./hooks/useAnalytics";
import Login from "./components/Login";
import HubPage from "./pages/HubPage";
import HomePage from "./pages/HomePage";
import InterviewPage from "./pages/InterviewPage";
import IELTSPage from "./pages/IELTSPage";
import IELTSSpeakingPage from "./pages/IELTSSpeakingPage";
import IELTSListeningPage from "./pages/IELTSListeningPage";
import IELTSWritingPage from "./pages/IELTSWritingPage";
import IELTSReadingPage from "./pages/IELTSReadingPage";
import PracticeSoundsPage from "./pages/PracticeSoundsPage";
import ProfilePage from "./pages/ProfilePage";
import LandingPage from "./pages/public/LandingPage";
import PricingPage from "./pages/public/PricingPage";
import ContactPage from "./pages/public/ContactPage";
import PrivacyPolicyPage from "./pages/public/PrivacyPolicyPage";
import TermsOfServicePage from "./pages/public/TermsOfServicePage";
import PaymentSuccessPage from "./pages/public/PaymentSuccessPage";
import PaymentCancelPage from "./pages/public/PaymentCancelPage";
import BecomeInvestorPage from "./pages/public/BecomeInvestorPage";
import AdminPage from "./pages/AdminPage";
import InvestorPage from "./pages/InvestorPage";
import NotFoundPage from "./pages/NotFoundPage";
import TokenLoginPage from "./pages/TokenLoginPage";
import FeedbackButton from "./components/FeedbackButton";
import EarlyBirdBanner from "./components/EarlyBirdBanner";
import IOSAppBanner from "./components/IOSAppBanner";
import LoadingScreen from "./components/LoadingScreen";
import "./App.css";

// Scroll to top on route change - fixes footer links not appearing to work
const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// Analytics tracker component - must be inside Router
const AnalyticsTracker: React.FC = () => {
  usePageTracking();
  return null;
};

// Protected route wrapper - redirects to login if not authenticated
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { currentUser, loading } = useAuth();

  // Show loading state while Firebase is initializing
  if (loading) {
    return <LoadingScreen message="Hang tight!" submessage="Getting everything ready for you" />;
  }

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Public route wrapper - redirects to app if already authenticated (for login page)
// Investors are not redirected to app
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, loading } = useAuth();
  const [checking, setChecking] = useState(true);
  const [isUserInvestor, setIsUserInvestor] = useState(false);

  useEffect(() => {
    const checkUserType = async () => {
      // Wait for auth to finish loading before checking
      if (loading) {
        return;
      }

      if (!currentUser) {
        setChecking(false);
        return;
      }

      try {
        const profile = await getUserProfile(currentUser.uid);
        setIsUserInvestor(isInvestor(profile.type));
      } catch {
        // If fetch fails, assume not investor
        setIsUserInvestor(false);
      }
      setChecking(false);
    };

    checkUserType();
  }, [currentUser, loading]);

  // Show content immediately while auth is loading
  // This allows login page to render without waiting for Firebase
  if (loading) {
    return <>{children}</>;
  }

  // Show loading indicator only when we have a user and are checking their type
  if (checking && currentUser) {
    return <LoadingScreen message="Almost there!" submessage="Checking your account" />;
  }

  // If logged in and not an investor, redirect to app
  if (currentUser && !isUserInvestor) {
    return <Navigate to="/app" replace />;
  }

  // If investor, redirect to investor page
  if (currentUser && isUserInvestor) {
    return <Navigate to="/investor" replace />;
  }

  // If not logged in, show the page
  return <>{children}</>;
};

const App: React.FC = () => {
  return (
    <Router>
      <ScrollToTop />
      <AnalyticsTracker />
      <EarlyBirdBanner />
      <IOSAppBanner />
      <FeedbackButton />
      <Routes>
        {/* Public Routes - accessible without login */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsOfServicePage />} />
        <Route path="/become-investor" element={<BecomeInvestorPage />} />

        {/* Admin Routes */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminPage />
            </ProtectedRoute>
          }
        />

        {/* Investor Routes */}
        <Route
          path="/investor"
          element={
            <ProtectedRoute>
              <InvestorPage />
            </ProtectedRoute>
          }
        />

        {/* Auth Routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />

        {/* Payment Callback Routes */}
        <Route path="/payment/success" element={<PaymentSuccessPage />} />
        <Route path="/payment/cancel" element={<PaymentCancelPage />} />

        {/* Token-based login (for mobile app redirect) */}
        <Route path="/auth/token/:token" element={<TokenLoginPage />} />

        {/* Protected App Routes */}
        <Route
          path="/app"
          element={
            <ProtectedRoute>
              <HubPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/speech"
          element={
            <ProtectedRoute>
              <HomePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/interview"
          element={
            <ProtectedRoute>
              <InterviewPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/ielts"
          element={
            <ProtectedRoute>
              <IELTSPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/ielts/speaking"
          element={
            <ProtectedRoute>
              <IELTSSpeakingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/ielts/listening"
          element={
            <ProtectedRoute>
              <IELTSListeningPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/ielts/writing"
          element={
            <ProtectedRoute>
              <IELTSWritingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/ielts/reading"
          element={
            <ProtectedRoute>
              <IELTSReadingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/sounds"
          element={
            <ProtectedRoute>
              <PracticeSoundsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/profile"
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />
        {/* Legacy route redirect - /interview -> /app/interview */}
        <Route
          path="/interview"
          element={<Navigate to="/app/interview" replace />}
        />

            {/* 404 Page */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Router>
  );
};

export default App;
